
// Import global hooks
import useFormHandler from "global/hooks/useFormHandler";
import { useToast } from "global/hooks/useToast";

// Import hooks
import useCustomProductsData from "../../hooks/useCustomProductsData";

// Import components
import CreateButton from "global/components/CreateButton";
import TwoColumnList from "global/components/TwoColumnList";

// Import utility functions
import { DisplayConfirmationDialog } from "global/utilities/DialogBox";
import { CloseIcon } from "global/strich_icons/StrichIcons";



// Render print area details on print area details page
const RenderPositionDetails = ({
    isOpen, 
    onClose,
    positionData,
    setSelectedPosition,

    selectedPrintAreaData, 
    selectedSurfaceData,
    selectedCustomProduct,  
    setSelectedPrintAreaData, 
    setSelectedSurfaceData,
    setSelectedCustomProduct, 

    handleOpenCustomizrClick,
}) => {



    //console.log("positionData: ", positionData);


    // Define toast options
    const { toastError } = useToast();



    // Custom hook for fetching and managing product data
    const {
        addUpdatePosition,
        deletePosition
    } = useCustomProductsData(); 

    

    // Use the form handler for this single object
    const {

        // Local form values for this specific surface
        formValues,      

        // Local change handler
        handleChange,    

        isFormPartlyEdited,

    } = useFormHandler({

        // Initial values are the surface object passed from the parent
        initialValues: positionData,

        // Sync when `surface` prop changes (e.g. editing a different item)
        syncInitialValues: true,
        convertBooleans: true,
        convertNumbers: true,
        
        // Optional validation - only when using handleSubmit 
        validate: (values) => {

        }

    });

    // Close the position editor, warning first if anything has changed
    const handleClosePosition = () => {

        // Close immediately when there is nothing to discard
        if (!isFormPartlyEdited()) {

            setSelectedPosition(null);
            onClose();

            return;

        }

        // Confirm before discarding unsaved position changes
        DisplayConfirmationDialog(

            "You have unsaved changes. Are you sure you want to close?",
            "Close",
            "Keep Editing",

            async function (is_confirmed) {

                if (is_confirmed) {

                    setSelectedPosition(null);
                    onClose();

                }

            }

        );

    };

    // Handle local form submit (in it's own file so it doesn't get confused with formValues in parent CustomProduct.js)
    const handleSubmitSavePosition = async (event) => {

        // Prevent default browser submit behaviour
        event.preventDefault();

        // Make the call from parent CustomProduct.js
        const response = await addUpdatePosition(selectedCustomProduct.code, selectedSurfaceData, selectedPrintAreaData.code, formValues, setSelectedCustomProduct, setSelectedSurfaceData, setSelectedPrintAreaData);
        
        // If parent returns a success flag
        if (response.success) {

            // Update the parent form so it doesn't revert back to the original data - need this if we don't want to go straight back to the custom product details
            //setSelectedPrintAreaData(formValues);

            // Make the successfully saved position the new editor baseline
            setSelectedPosition(formValues);

        } else {

            // Toast fail message
            toastError({ title: "Error", message: "Could not update position: " + response.error_message });

        }

        // If parent indicates failure, do nothing so the values stay on screen

    };



    // Function to handle clicking delete position
    const handleDeletePosition = async () => {

        // Display a confrimation dialog
        DisplayConfirmationDialog(

            // Dialog message
            "Are you sure you want to delete this position: " + positionData.name + "?",

            // Confirm button title
            "Delete",

            // Cancel button title
            "Cancel",

            // Returned response from the dialog box
            async function (is_confirmed) {
            
                // Return early if they cancel
                if (is_confirmed) {

                    // Make the call
                    const response = await deletePosition(selectedCustomProduct.code, selectedSurfaceData, selectedPrintAreaData.code, positionData.code, setSelectedCustomProduct, setSelectedSurfaceData, setSelectedPrintAreaData);
                  
                    
                    // If successful
                    if (response.success) {

                        // Clear the position data
                        setSelectedPosition(null);

                        // Close the pop up
                        onClose();

                    } else {

                        // Toast fail message
                        toastError({ title: "Error", message: "Could not delete position: " + response.error_message });

                    }
                    
                };

            }
            
        );

    };



    // If the pop up isn't open return early
    if (!isOpen) return null;



    return (

        <div className="popup-overlay">
            
            <div className="popup-container popup-container--small" style={{height: "auto"}}>

                {/* This is the modal header */}
                <div className="popup__header">

                    {/* This is the title area */}
                    <div className="popup__headerLeft">

                        {/* This is the parent title */}
                        <h2 className="popup__title">

                            Position Details

                        </h2>

                        {/* This is the parent subtitle */}
                        <p className="popup__subtitle">

                            {formValues.code ? formValues.code : ""} 

                        </p>

                    </div>

                    {/* This is the header actions */}
                    <div className="popup__headerRight">

                        {/* This is the close button */}
                        <button type="button" className="popup__close" onClick={() => handleClosePosition()} title="Close">
                            <CloseIcon />
                        </button>

                    </div>

                </div>


                <div className="popup-content popup-content__body">

                    <div>

                        <form id="positionDetailsForm" onSubmit={handleSubmitSavePosition}>

                            <TwoColumnList 
                                property={<label>Name</label>} 
                                value={<input 
                                    name="name" 
                                    type="text" 
                                    placeholder="Name" 
                                    value={formValues?.name || ""}
                                    onChange={handleChange}
                                    required
                                />} 
                            />
                            <TwoColumnList 
                                property={<label>Code</label>} 
                                value={<input 
                                    name="code" 
                                    type="text" 
                                    placeholder="Code"
                                    value={formValues?.code || ""}
                                    onChange={handleChange}
                                    required 
                                    disabled={positionData}
                                />} 
                            />
                            <TwoColumnList 
                                property={<label>Description</label>} 
                                value={<textarea name="description" rows="1" cols="30"  value={formValues?.description || ""} onChange={handleChange} placeholder="Description"></textarea>} 
                            />
                            <TwoColumnList 
                                property={<label>Width (0-1)</label>} 
                                value={<input 
                                    name="width" 
                                    type="number"
                                    step="any"
                                    placeholder="Width"
                                    value={formValues?.width || ""}
                                    onChange={handleChange}
                                    required 
                                    disabled={positionData}
                                />} 
                            />
                            <TwoColumnList 
                                property={<label>Height (0-1)</label>} 
                                value={<input 
                                    name="height" 
                                    type="number"
                                    step="any"
                                    placeholder="Height"
                                    value={formValues?.height || ""}
                                    onChange={handleChange}
                                    required 
                                    disabled={positionData}
                                />} 
                            />
                            <TwoColumnList 
                                property={<label>Centre X (0-1)</label>} 
                                value={<input 
                                    name="centre_x" 
                                    type="number"
                                    step="any"
                                    placeholder="Centre X"
                                    value={formValues?.centre_x || ""}
                                    onChange={handleChange}
                                    required 
                                    disabled={positionData}
                                />} 
                            />
                            <TwoColumnList 
                                property={<label>Centre Y (0-1)</label>} 
                                value={<input 
                                    name="centre_y" 
                                    type="number"
                                    step="any"
                                    placeholder="Centre Y"
                                    value={formValues?.centre_y || ""}
                                    onChange={handleChange}
                                    required 
                                    disabled={positionData}
                                />} 
                            />



                        </form>
                

                        {/* Tab content */}
                        {/* Scrollable content area */}
                        <div className="tabs-scroll-container">

                            <div className="tabs-content">


                            </div>

                        </div>

                        {/* Footer actions */}
                        <div className="tabs-footer tabs-footer--fill-no-background horizontal-align-content-center flex-justify-left-right">

                            <div>

                                {/* Only show delete button if we have positionData, if we don't then they're adding a new one */}
                                {positionData &&
                                
                                    <CreateButton title="Delete Position" isDelete={true} tooltip="Delete Position" size="m" icon="/images/icon_bin.png" onClick={() => handleDeletePosition(selectedCustomProduct.code, selectedSurfaceData.details.code, setSelectedPrintAreaData.code)} />

                                }

                            </div>

                            <div className="horizontal-align-content-center">

                                <CreateButton type="submit" title="Edit Position" size="m" onClick={() => handleOpenCustomizrClick("position")} />

                                {isFormPartlyEdited() &&

                                    <CreateButton
                                        type="submit"
                                        title={positionData ? "Save changes" : "Create Position"}
                                        size="m"
                                        formID="positionDetailsForm"
                                    />

                                }

                                <CreateButton
                                    type="button"
                                    title="Close"
                                    invert
                                    size="m"
                                    onClick={() => handleClosePosition()}
                                />

                            </div>
                            
                        </div>
                        
                    </div>

                </div>

            </div>

        </div>

    );

}; export default RenderPositionDetails;
